import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IToken } from '../../../types/my-types';
import { AppContext } from '../../../App';
import MarkField from '../../tool/form/MarkField';
import { useCreateCorrection } from '../../hooks/useCreateCorrection';
import errorResponses from '../../../errorResponses';

import getTokensForCorrection from '../../../utils/getTokensForCorrection';
import canSubmitCorrection from '../../../utils/canSubmitCorrection';
import { IFieldReview } from './FieldList';
import getPageFieldIsOnFromTokens from '../../../utils/getPageFieldIsOnFromTokens';
import { IValidationErrorByExtractionDetails } from '../../../types/validation-types';

interface CorrectionFieldProps {
  field: IFieldReview;
  fieldsCount: number;
  fieldId: string;
  index: number;
  ebId: string;
  extractionId: string;
  validationErrorsForThisField: IValidationErrorByExtractionDetails[] | [];
  highlightField?: boolean;
  isAdditionalField?: boolean;
  //	correction: ICorrection | undefined;
  fields: string[];
  readOnly?: boolean;
}

const NO_DATA_TYPE_CONFIG = {};
const NO_GLYNT_TAGS = [''];

const CorrectionField = (props: CorrectionFieldProps) => {
  const {
    field,
    fields,
    fieldId,
    fieldsCount,
    index,
    ebId,
    extractionId,
    validationErrorsForThisField,
    highlightField,
    isAdditionalField,
    readOnly = false,
  } = props;
  const { setToast } = React.useContext(AppContext);
  const cache = useQueryClient();

  const mutationCreateCorrection = useCreateCorrection(true, true, {
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Correction Saved',
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['extraction', extractionId],
      });
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToSaveCorrection,
      });
    },
  });

  const initValue = field.content || '';

  const pageIndex = getPageFieldIsOnFromTokens(field.tokens);

  const saveCorrection = async (value: string, tokens: IToken[]) => {
    const saveTokens = getTokensForCorrection(value, field, tokens);

    if (canSubmitCorrection(initValue, value, field.tokens, saveTokens)) {
      const newCorrection = { content: value, tokens: saveTokens };
      await mutationCreateCorrection.mutate({
        correction: newCorrection,
        extraction: extractionId,
        field: field.label,
      });
    }
    /*
      if (correction) {
        await updateCorrection({
          variables: {
            dataPoolId,
            id: correction.id,
            input: { correction: newCorrection },
          },
          refetchQueries: [
            {
              query: GET_EXTRACTION,
              variables: { dataPoolId, id: extraction.id },
            },
          ],
        });
      } else {
			await createCorrection({
				variables: {
					dataPoolId,
					input: {
						extraction: extraction.id,
						field,
						correction: newCorrection,
					},
				},
				update: (store, { data }) => {
					const correctionsCache: any = store.readQuery({
						query: GET_ALL_CORRECTIONS,
					});
					store.writeQuery({
						query: GET_ALL_CORRECTIONS,
						data: {
							corrections: {
								...correctionsCache.corrections,
								results: [
									...correctionsCache.corrections.results,
									data.createCorrection,
								],
							},
						},
					});
				},
				refetchQueries: [
					{
						query: GET_EXTRACTION,
						variables: { dataPoolId, id: extraction.id },
					},
				],
			});
			//  }
		} catch (error) {
			const errorCode = (error.networkError as any).statusCode;
			if (errorCode === 503) {
				setMaintenance(true);
			} else {
				
			}
		}
        */
  };

  const latestSaveCorrection = React.useRef(saveCorrection);
  latestSaveCorrection.current = saveCorrection;
  const save = React.useCallback(
    (value: string, tokens: IToken[]) => latestSaveCorrection.current(value, tokens),
    []
  );

  // retro compatibility if fieldData is null
  const fieldDataTypeConfig = field.data_type_config ? field.data_type_config : NO_DATA_TYPE_CONFIG;

  return (
    <MarkField
      ebId={ebId}
      extractionId={extractionId}
      field={field.label}
      fields={fields}
      fieldsCount={fieldsCount}
      fieldTags={field.tags}
      fieldDataType={field.data_type}
      fieldDataTypeConfig={fieldDataTypeConfig}
      fieldId={fieldId}
      validationConfig={field.validation_config}
      glyntTags={field.glynt_tags || NO_GLYNT_TAGS}
      index={index}
      pageIndex={pageIndex === 0 ? pageIndex : pageIndex || -1}
      initValue={field.content || ''}
      save={save}
      isMutationLoading={mutationCreateCorrection.isPending}
      isMutationError={mutationCreateCorrection.isError}
      isReview={true}
      validationErrorsForThisField={validationErrorsForThisField}
      highlightField={highlightField}
      isAdditionalField={isAdditionalField}
      readOnly={readOnly}
    />
  );
};

export default React.memo(CorrectionField);
